import {
  useGetAnswersForMetricsOnCompanyLevelQuery,
  useGetCompanyLevelMdrmAnswersQuery,
  useGetAssessmentCurrencyConversionQuery,
  useGetRequiredGroupMaterialMetricsQuery,
  useGetSubMaterialMetricsOnGroupLevelQuery,
  useGetSubAnswersOtherQuery,
  useGetSubAnswersEnvironmentQuery,
  useEsrsAssessmentMaterialStandardsQuery,
} from 'models';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  CategoriesType,
  EsrsReportCategory,
  EsrsReportData,
  EsrsReportDisclosureRequirement,
  EsrsReportStandard,
  MaterialReportDisclosure,
  MaterialReportMetric,
  MaterialReportStandard,
} from '../Report.types';
import { useAssessmentReportingUnits } from 'containers/Esrs/EsrsAssessment.hooks';
import {
  AggregatedMetricsTableData,
  DREnums,
  MaybeHasChild,
  getNestedRows,
  isFullMetricOnGroupLevel,
} from '../../DisclosureRequirements';
import { getMetricMdrmAnswers, getMetricResult } from '../MetricAnswerCalculation';
import { QuestionType } from 'utils/scores/questions';
import { useCompanyType } from 'utils/hooks';
import { ORDER_OF_CATEGORIES } from 'utils/categoriesOrder';
import { DataCollectionLevel } from '../../DataCollection';
import { uniq } from 'lodash';
import { MARKED_GENERAL } from '../ReportUtils';
import { useGetReportMaterialStandards } from '../ReportCloudFunctions.hooks';
import { environmentDRRefs, getIsMetricLocked } from 'containers/Esrs/utils';
import { GroupMaterialMetrics } from '../../DisclosureRequirements/Metrics/AggregatedMetrics/AggregatedMetricsUtils';
import { AssessableMetrics } from '../../DisclosureRequirements/Metrics/Metrics';
import { addSubsidiaryTags, filterQualitativeChildren, getLatestUpdated } from '../Report.hooks';

const addPercentagesToMetricAndFilterOptedOut = (
  metric: AggregatedMetricsTableData,
  optedOutAnswers: string[]
): AggregatedMetricsTableData => {
  const calculatePercentage = (
    child?: AggregatedMetricsTableData,
    parent?: AggregatedMetricsTableData
  ) => {
    const percent = ((child?.result?.Year ?? 0) / (parent?.result?.Year ?? 1)) * 100;
    return isNaN(percent) ? '0' : percent.toFixed(2).replace(/[.,]00$/, '');
  };
  const mapSubrows = (
    subRow?: AggregatedMetricsTableData,
    parent?: AggregatedMetricsTableData
  ): AggregatedMetricsTableData => {
    if (subRow?.subRows) {
      return {
        ...subRow,
        percentage: subRow?.metric.showAsPercentage
          ? calculatePercentage(subRow, parent)
          : undefined,
        subRows: subRow.subRows
          .filter(
            (row) => !optedOutAnswers?.find((metricRef) => metricRef === row.metric.reference)
          )
          ?.map((sub) => mapSubrows(sub, subRow)),
      };
    }
    return {
      ...subRow,
      metric: subRow?.metric ?? ({} as AggregatedMetricsTableData['metric']),
      percentage: subRow?.metric.showAsPercentage ? calculatePercentage(subRow, parent) : undefined,
    };
  };
  return {
    ...metric,
    percentage: metric.metric.showAsPercentage ? `100` : undefined,
    subRows: metric.subRows
      ?.filter((row) => !optedOutAnswers?.find((metricRef) => metricRef === row.metric.reference))
      ?.map((subRow) => mapSubrows(subRow, metric)),
  };
};

// Hook used specifically for WORD DOC
export const useEsrsReportData = (isClicked: boolean) => {
  const { isGroup } = useCompanyType();
  const { esrsAssessmentId = '' } = useParams();
  const { reportingUnitAssessments } = useAssessmentReportingUnits(esrsAssessmentId);
  const { data: currencyConversionData } = useGetAssessmentCurrencyConversionQuery({
    fetchPolicy: 'network-only',
    variables: {
      assessmentId: esrsAssessmentId,
    },
    skip: !isGroup,
  });

  const [companyReportingUnitId, firstBUReportingUnitId] = useMemo(
    () => [
      reportingUnitAssessments.find((ru) => ru.isCompanyLevel)?.id ?? '',
      reportingUnitAssessments.filter((ru) => !ru.isCompanyLevel)?.[0]?.id ?? '',
    ],
    [reportingUnitAssessments]
  );

  const { data: esrsAssessmentData, loading: assessmentLoading } =
    useEsrsAssessmentMaterialStandardsQuery({
      variables: {
        esrsAssessmentId,
      },
      fetchPolicy: 'cache-first',
      skip: !esrsAssessmentId,
    });

  const { data: reportingUnitsAnswers, loading: reportingUnitsAnswersLoading } =
    useGetAnswersForMetricsOnCompanyLevelQuery({
      variables: {
        esrsAssessmentId,
      },
      fetchPolicy: 'cache-first',

      skip: !esrsAssessmentId || !companyReportingUnitId,
    });

  const { data: subAnswersEnvironmentData, loading: subAnswersEnvironmentLoading } =
    useGetSubAnswersEnvironmentQuery({
      variables: { esrsAssessmentId, environmentDisclosures: environmentDRRefs },
      fetchPolicy: 'cache-first',
      skip: !isGroup || !esrsAssessmentId,
    });

  const isCollectOnly = esrsAssessmentData?.esrsAssessment?.collectOnly ?? false;

  const { data: parentAssessmentData, loading: parentAssessmentDataLoading } =
    useGetRequiredGroupMaterialMetricsQuery({
      variables: {
        parentAssessmentId: esrsAssessmentData?.esrsAssessment?.parentAssessmentId,
      },
      skip: !isCollectOnly || !esrsAssessmentData?.esrsAssessment?.parentAssessmentId,
    });

  const groupMaterialMetrics = isCollectOnly
    ? (parentAssessmentData?.esrs_MaterialMetric ?? [])
    : [];

  const groupMaterialMetricsRefs = groupMaterialMetrics.map((mm) => mm.metric.reference);
  const groupMaterialStandardsRefs = uniq(
    groupMaterialMetrics.flatMap((mm) => mm.metric.requirement.group.standardRef)
  );

  const { data: subAnswersOtherData, loading: subAnswersOtherLoading } = useGetSubAnswersOtherQuery(
    {
      variables: { esrsAssessmentId, environmentDisclosures: environmentDRRefs },
      fetchPolicy: 'cache-first',
      skip: !isGroup || !esrsAssessmentId,
    }
  );

  const { data: subMaterialMetricsData, loading: subMaterialMetricsLoading } =
    useGetSubMaterialMetricsOnGroupLevelQuery({
      variables: { esrsAssessmentId },
      fetchPolicy: 'cache-first',
      skip: !isGroup || !esrsAssessmentId,
    });

  const groupAnswers = useMemo(() => {
    const reportingUnits =
      subAnswersEnvironmentData?.EsrsAssessment_by_pk?.subsidiaryAssessments?.map((assessment) => ({
        ...assessment,
        reportingUnits: assessment.reportingUnits?.map((rua) => ({
          ...rua,
          answers: [
            ...rua.answers,
            ...(subAnswersOtherData?.EsrsAssessment_by_pk?.subsidiaryAssessments
              ?.find((sa) => sa.id === assessment.id)
              ?.reportingUnits?.find((ru) => ru.id === rua.id)?.answers ?? []),
          ],
        })),
      })) ?? [];
    const data = {
      EsrsAssessment_by_pk: {
        ...subMaterialMetricsData?.EsrsAssessment_by_pk,
        id: subMaterialMetricsData?.EsrsAssessment_by_pk?.id ?? '',
        subsidiaryAssessments:
          subMaterialMetricsData?.EsrsAssessment_by_pk?.subsidiaryAssessments?.map((a) => ({
            ...a,
            reportingUnits:
              reportingUnits?.find((assessment) => assessment.id === a.id)?.reportingUnits ?? [],
          })) ?? [],
      },
    };
    return data ?? [];
  }, [subAnswersEnvironmentData, subAnswersOtherData, subMaterialMetricsData]);

  const companyLevelRU = reportingUnitsAnswers?.EsrsAssessment_by_pk?.reportingUnits.find(
    (ru) => ru.isCompanyLevel
  );
  const answers = companyLevelRU?.answers ?? [];
  const optedOutAnswers = companyLevelRU?.optedOutAnswers.map((a) => a.metricRef) ?? [];

  const { data, loading: materialStandardsLoading } = useGetReportMaterialStandards({
    esrsAssessmentId,
    isClicked,
    groupMaterialMetricsRefs,
    groupMaterialStandardsRefs,
  });

  const { data: mdrmAnswersData, loading: mdrmAnswersLoading } = useGetCompanyLevelMdrmAnswersQuery(
    {
      variables: {
        reportingUnitId: companyReportingUnitId,
      },
      skip: !companyReportingUnitId,
    }
  );

  const allMdrmAnswers = useMemo(
    () => mdrmAnswersData?.esrs_DatapointMdrm ?? [],
    [mdrmAnswersData]
  );

  const { companyName, reportingYear } = useMemo(() => {
    const assessmentData = esrsAssessmentData?.esrsAssessment;
    return {
      companyName: assessmentData?.company.name,
      reportingYear: assessmentData?.reportingYear,
    };
  }, [esrsAssessmentData]);

  const materialStandards = useMemo(
    () => data?.EsrsAssessment_by_pk?.materialStandards ?? [],
    [data]
  );
  const allMaterialMetrics = materialStandards
    .flatMap((std) => std.materialMetrics)
    .filter(
      (mm) => mm.materialStandard.standardRef === mm.metric.requirement.group.standard.reference
    );

  const categories: CategoriesType = useMemo(() => {
    const set = [
      ...new Set(
        materialStandards.map((std) =>
          JSON.stringify({
            title: std.standard.category.title,
            ref: std.standard.categoryRef,
          })
        )
      ),
    ].map((s) => JSON.parse(s));

    const generalDisclosure = set.find((category) => category.ref === 'general');
    const otherCategories = set.filter((category) => category.ref !== 'general');
    otherCategories.sort((a, b) => a.title.localeCompare(b.title));
    const cats = generalDisclosure ? [generalDisclosure, ...otherCategories] : otherCategories;
    return cats.sort((a, b) => ORDER_OF_CATEGORIES?.[a.title] - ORDER_OF_CATEGORIES?.[b.title]);
  }, [materialStandards]);

  const answersMap = useMemo(() => {
    const map = new Map();
    answers?.forEach((ans) => {
      map.set(ans.metricRef, ans.datapoints.find((dp) => !!dp.value)?.value);
    });
    return map;
  }, [answers]);

  const checkIfCompleted = (
    metric: MaterialReportMetric,
    textAnswer?: string,
    result?: AggregatedMetricsTableData
  ) => {
    if (metric.metric.metricType === QuestionType.Decimal_) {
      return { completed: !!result?.result?.Year, answered: !!result?.result?.Year };
    }
    let completed = true;
    let answered = !!textAnswer;

    const checkChildren = (childrenMetrics: MaterialReportMetric['childrenMetrics']) => {
      for (const { childMetric } of childrenMetrics) {
        if (childMetric) {
          const childAnswer = answersMap.get(childMetric.reference);
          const isCurrentMetricCompleted = !!childAnswer;
          completed = completed && isCurrentMetricCompleted;
          answered = answered || isCurrentMetricCompleted;

          if (childMetric.childrenMetrics?.length > 0) {
            checkChildren(childMetric.childrenMetrics as MaterialReportMetric['childrenMetrics']);
          }
        }
      }
    };

    if (metric.childrenMetrics?.length) {
      checkChildren(metric.childrenMetrics);
    } else {
      completed = !!textAnswer;
    }

    return { completed, answered };
  };

  const getMetricData = (metric: MaterialReportMetric, standardId: string, standardRef: string) => {
    const filteredMetric =
      metric.metric.metricType !== QuestionType.Decimal_
        ? metric
        : filterQualitativeChildren(metric);

    const metricObject = {
      ...metric.metric,
      childrenMetrics: filteredMetric.childrenMetrics,
      materialMetrics: [metric],
    };

    const metricWithTags = addSubsidiaryTags(
      metricObject,
      groupAnswers?.EsrsAssessment_by_pk?.subsidiaryAssessments ?? [],
      standardRef
    );

    const textAnswer = answersMap.get(metric.metricRef);

    const answer = answers?.find((a) => a.metricRef === metric.metricRef);
    const datapoint = getLatestUpdated(answer?.datapoints);
    const textBefore = datapoint?.metadata?.before?.text;
    const textAfter = datapoint?.metadata?.after?.text;
    const isBeforeVisible = datapoint?.metadata?.before?.isVisible === true && !!textBefore;
    const isAfterVisible = datapoint?.metadata?.after?.isVisible === true && !!textAfter;
    const isLocked = getIsMetricLocked(metricObject.unlockCondition, datapoint?.factValue ?? '');
    const isMdrmDisclosed = datapoint?.isMdrmDisclosed;
    const hasOptedOut = optedOutAnswers.some((metricRef) => metricRef === metric.metricRef);

    const materialMetrics = groupAnswers?.EsrsAssessment_by_pk?.subsidiaryAssessments.flatMap(
      (company) =>
        company.materialStandards
          .find((ms) => ms.standardRef === standardRef)
          ?.materialMetrics.find((mm) => mm?.metricRef === metric.metricRef)
    );

    const nestedMetric = getNestedRows({
      metric: metricWithTags as AssessableMetrics[number],
      materialStandardId: standardId,
      isGroup,
      materialMetrics: !isFullMetricOnGroupLevel(metricWithTags as MaybeHasChild)
        ? (materialMetrics as GroupMaterialMetrics)
        : undefined,
    });
    const metricResult = getMetricResult({
      metric: metricWithTags as AssessableMetrics[number],
      nestedMetric,
      isGroup,
      reportingUnitsAnswers,
      groupAnswers,
      standardRef,
      materialStandardId: standardId,
      currencyConversionData: currencyConversionData?.esrs_AssessmentCurrencyConversion,
    });

    const result = addPercentagesToMetricAndFilterOptedOut(metricResult, optedOutAnswers);

    const mdrmAnswers = getMetricMdrmAnswers(metricObject, allMdrmAnswers);
    const { completed, answered } = checkIfCompleted(metric, textAnswer, result);

    return {
      metric,
      textAnswer: metric.metric.metricType !== QuestionType.Decimal_ ? textAnswer : undefined,
      tableData: result,
      reportingUnitId:
        metric.dataCollection === DataCollectionLevel.company
          ? companyReportingUnitId
          : firstBUReportingUnitId,
      completed,
      isAnswered: answered,
      mdrmAnswers: mdrmAnswers,
      isBeforeVisible,
      isAfterVisible,
      textBefore,
      textAfter,
      isLocked,
      isMdrmDisclosed,
      hasOptedOut,
    };
  };

  const getDisclosureData = (
    dr: MaterialReportDisclosure,
    category: string,
    standardTitle: string,
    standardId: string,
    standardRef: string
  ): EsrsReportDisclosureRequirement => {
    const filteredDrs = materialStandards.find(
      (stand) =>
        stand.standard.category.title === category && stand.standard.title === standardTitle
    );
    const materialMetrics =
      standardRef === 'ESRS-2' ? allMaterialMetrics : filteredDrs?.materialMetrics;
    const metrics =
      materialMetrics
        ?.filter((met) => dr.metrics.find((rq) => met.metricRef === rq.reference))
        .filter((metric) => {
          const isTopLevelMetric = metric.metric.parentMetrics.length === 0;
          const isAssessable = metric.metric.isAssessable;
          if (metric.metric.metricType === QuestionType.Decimal_) {
            return isTopLevelMetric ? isAssessable : true;
          }
          return true;
        })
        .map((metric) => getMetricData(metric, standardId, standardRef)) ?? [];
    return {
      title: dr.title,
      type: dr.type,
      reference: dr.reference,
      order: dr.order,
      description: dr.description,
      additionalTypes: dr.additionalTypes,
      isHidden:
        filteredDrs?.materialMetrics
          .filter((metric) => dr.metrics.find((met) => met.reference === metric.metricRef))
          .every((metric) => metric.isHidden) ?? false,
      metrics: metrics?.filter((m) => m.hasOptedOut !== true) ?? [],
      targets: dr.type === DREnums.target ? dr.targets : [],
      actions: dr.type === DREnums.action ? dr.actions : [],
      metricsIncludingOptedOut: metrics,
    };
  };

  const getStandardsData = (
    standard: MaterialReportStandard,
    category: string
  ): EsrsReportStandard => {
    const materialMetrics =
      standard.standardRef === 'ESRS-2'
        ? allMaterialMetrics.filter(
            (metric) =>
              metric.materialStandard.standardRef === 'ESRS-2' ||
              MARKED_GENERAL.includes(metric.metric.disclosureRequirementRef)
          )
        : standard.materialMetrics.filter(
            (met) => !MARKED_GENERAL.includes(met.metric.disclosureRequirementRef)
          );

    const disclosureRequirementsMap = materialMetrics.reduce((acc, mm) => {
      const standardDR = standard.standard.disclosureRequirementGroups
        .flatMap((req) => req.requirements)
        .find((r) => r.reference === mm.metric.disclosureRequirementRef);
      const disclosureRequirement = {
        reference: mm.metric.disclosureRequirementRef,
        title: mm.metric.requirement.title,
        shortTitle: mm.metric.requirement.shortTitle,
        order: mm.metric.requirement.order,
        type: mm.metric.requirement.type,
        additionalTypes: mm.metric.requirement.additionalTypes,
        metrics: [],
        targets: standardDR?.type === DREnums.target ? standardDR.targets : [],
        actions: standardDR?.type === DREnums.action ? standardDR.actions : [],
      };
      if (!acc.has(disclosureRequirement.reference)) {
        acc.set(disclosureRequirement.reference, disclosureRequirement);
      }

      acc.get(disclosureRequirement.reference).metrics.push(mm.metric);

      return acc;
    }, new Map());

    const disclosureRequirementsArray: MaterialReportDisclosure[] = Array.from(
      disclosureRequirementsMap.values()
    );

    const disclosureRequirements = disclosureRequirementsArray
      .flat()
      .sort((a, b) => a.order - b.order || a.reference.localeCompare(b.reference));

    return {
      id: standard.id,
      title: standard.standard.title,
      reference: standard.standardRef,
      targetQuestions: standard.standard.targetQuestions,
      actionQuestions: standard.standard.actionQuestions,
      order: standard.standard.order,
      disclosureRequirements: disclosureRequirements.map((dr) =>
        getDisclosureData(
          dr as MaterialReportDisclosure,
          category,
          standard.standard.title,
          standard.id,
          standard.standardRef
        )
      ),
    };
  };

  const getCategoryData = (category: CategoriesType[number]): EsrsReportCategory => {
    const filteredStandards = materialStandards.filter(
      (std) => std.standard.category.title === category.title
    );

    return {
      title: category.title,
      reference: category.ref,
      standards: filteredStandards
        .map((std) => getStandardsData(std, category.title))
        .sort((a, b) => a.order - b.order),
    };
  };

  const reportData: EsrsReportData = useMemo(
    () => ({
      categories: categories?.map((category) => getCategoryData(category)) ?? [],
      companyName: companyName ?? '',
      reportingYear: reportingYear ?? new Date().getUTCFullYear(),
      materialStandards: esrsAssessmentData?.esrsAssessment?.materialStandards,
    }),
    [
      categories,
      answers,
      data,
      companyName,
      reportingYear,
      reportingUnitsAnswers,
      materialStandards,
      subAnswersOtherData,
      subAnswersEnvironmentData,
      subMaterialMetricsData,
    ]
  );

  const loading = useMemo(() => {
    return (
      materialStandardsLoading ||
      reportingUnitsAnswersLoading ||
      assessmentLoading ||
      mdrmAnswersLoading ||
      parentAssessmentDataLoading ||
      subAnswersEnvironmentLoading ||
      subAnswersOtherLoading ||
      subMaterialMetricsLoading
    );
  }, [
    materialStandardsLoading,
    reportingUnitsAnswersLoading,
    assessmentLoading,
    mdrmAnswersLoading,
    parentAssessmentDataLoading,
    subAnswersEnvironmentLoading,
    subAnswersOtherLoading,
    subMaterialMetricsLoading,
  ]);

  return { data: reportData, loading };
};
